import type { FilterOptions } from "components/types/filter-options";
import type { WorkingFilters } from "components/types/working-filters";
import { ALL_HEARTS_SHORTLIST_ID, nonAttributeFilterKeys } from "rtr-constants";
import { activeFilters, filterToString } from "helpers/filter-helpers";
import { useAppSelector, type RootState } from "../../../util/store-types";
import { useEffect, useRef, useState } from "react";
import { replaceFilterOptions } from "helpers/grid-epic-filter-options";
import type { FeatureFlags } from "components/types/flags";
import { isStorefrontNext } from "helpers/environment-helpers";
import type { StorefrontShortlist } from "components/types/shortlists";
import type { UserFilters } from "components/types/user-problems";
import { createSelector } from "@reduxjs/toolkit";

export type DisplayedFilter = {
  displayedValue: string;
  filterKey: string;
  filterValue: string;
};

class OrderedFilters {
  existingFilters: DisplayedFilter[];
  //These are only required for legacy Ruby Rental (non-Shortlist/Swaps) grids
  //This logic can be removed once we're fully off of Ruby
  accessoryOnly?: boolean;
  path: string;
  featureFlags: Record<string, boolean>;
  shouldReplaceFilterOptions: boolean;

  static readonly isSameFilter = (filter1: DisplayedFilter, filter2: DisplayedFilter) =>
    filter1.filterKey === filter2.filterKey && filter1.filterValue === filter2.filterValue;
  static readonly getFilterAtIndex = (filter: DisplayedFilter, filters: DisplayedFilter[]) => {
    return filters.findIndex(f => this.isSameFilter(filter, f));
  };

  constructor(
    initialWorkingFilters: WorkingFilters,
    filterOptions: FilterOptions,
    shortlists: StorefrontShortlist[],
    accessoryOnly: boolean | undefined,
    path: string,
    featureFlags: Record<string, boolean>,
    isSwapsGrid: boolean | undefined
  ) {
    this.accessoryOnly = accessoryOnly;
    this.path = path;
    this.featureFlags = featureFlags;
    this.shouldReplaceFilterOptions = !isSwapsGrid && !isStorefrontNext();

    this.existingFilters = this.getDisplayedFilters(initialWorkingFilters, filterOptions, shortlists);

    this.getOrderedFilters = this.getOrderedFilters.bind(this);
    this.getDisplayedFilterCount = this.getDisplayedFilterCount.bind(this);
  }

  getOrderedFilters() {
    return this.existingFilters;
  }
  getDisplayedFilterCount() {
    return this.existingFilters.length;
  }

  getFilterOptions = (filterOptions: FilterOptions) =>
    this.shouldReplaceFilterOptions
      ? replaceFilterOptions(filterOptions, this.accessoryOnly, this.path, this.featureFlags)
      : filterOptions;

  private getShortlistFilters(filters: WorkingFilters, shortlists: StorefrontShortlist[] = []) {
    if (!filters.productCollections) {
      return [];
    }

    return (
      shortlists
        // NW [EXPLANATION] 8/20/19: exclude the All Hearts filter from Shortlist filters - it behaves differently than a shortlist
        .filter(shortlist => shortlist.id !== ALL_HEARTS_SHORTLIST_ID)
        .filter(shortlist => filters.productCollections?.includes(shortlist.id))
        .map(shortlist => {
          return {
            filterValue: shortlist.id,
            displayedValue: shortlist.name,
            filterKey: "productCollections",
          };
        })
    );
  }

  private getDisplayedFilters(
    filters: WorkingFilters,
    filterOptions: FilterOptions,
    shortlists: StorefrontShortlist[]
  ) {
    //displays all selected filters except core filters, core filters are handled separately at the top of the filter pane. Not sure why but zip code comes back formatted two different ways.
    const workingFiltersToDisplay = Object.fromEntries(
      Object.entries(filters).filter(([key]) => !nonAttributeFilterKeys.some(k => k === key))
    );

    const baseFilters = activeFilters(workingFiltersToDisplay)
      .map(filter => {
        const displayedValue = filterToString(
          filter.filterValue,
          filters,
          this.getFilterOptions(filterOptions),
          filter.filterKey
        );

        if (!displayedValue) {
          return;
        }

        return {
          ...filter,
          displayedValue,
        };
      })
      .filter(f => typeof f !== "undefined");

    return [...baseFilters, ...this.getShortlistFilters(filters, shortlists)];
  }

  private getNewFilters(updatedFilters: DisplayedFilter[]) {
    return updatedFilters.filter(uf => !this.existingFilters.some(ef => OrderedFilters.isSameFilter(ef, uf)));
  }

  private getRemovedFilters(updatedFilters: DisplayedFilter[]) {
    return this.existingFilters.filter(ef => !updatedFilters.some(uf => OrderedFilters.isSameFilter(ef, uf)));
  }

  onFilterChange(filters: WorkingFilters, filterOptions: FilterOptions, shortlists: StorefrontShortlist[] = []) {
    const updatedFilters = this.getDisplayedFilters(filters, filterOptions, shortlists);

    const newFilters = this.getNewFilters(updatedFilters);
    const removedFilters = this.getRemovedFilters(updatedFilters);

    if (!updatedFilters.length) {
      this.existingFilters = [];
      return;
    } else if (!this.existingFilters.length) {
      this.existingFilters.push(...updatedFilters);
      return;
    }

    if (removedFilters.length) {
      const indicesToRemove = removedFilters.map(rf => OrderedFilters.getFilterAtIndex(rf, this.existingFilters));
      this.existingFilters = this.existingFilters.filter((_, i) => !indicesToRemove.includes(i));
    }
    if (newFilters.length) {
      this.existingFilters.push(...newFilters);
    }
  }
}

const selectSwapsFilters = (state: RootState) => state.userProblems?.userFilters?.filter;
/**
 * sbenedict 7/25
 * We pass the return value of this selector as a dependency to useEffect, but it returns a unique reference each time.
 * Memoize the parsed value and only update when Redux filters have changed.
 * Ideally we'd do the data manipulation elsewhere, but Swaps are legacy code and due for a more in-depth rewrite.
 */
const swapsFiltersSelector = createSelector(selectSwapsFilters, swapsFilters => {
  if (!swapsFilters) {
    return {};
  }

  const newFilters: UserFilters["filter"] = {};
  Object.keys(swapsFilters).forEach(key => {
    const newKey = key.replace("products.", "");
    if (swapsFilters[key]) {
      newFilters[newKey] = swapsFilters[key];
    }
  });

  return newFilters;
});

export const useDisplayedFilters = () => {
  const [displayedFilters, setDisplayedFilters] = useState<DisplayedFilter[]>([]);
  const [displayedFilterCount, setDisplayedFilterCount] = useState(0);

  const workingFilters = useAppSelector(state => state.workingFilters);
  const filterOptions = useAppSelector(state => state.filterOptions);
  const shortlists = useAppSelector(state => state.shortlists);

  //sbenedict 7/25
  //Yes, this is hacky - we use a very old set of 10+ feature flags to generate Ruby filters
  //I'd prefer to not have to request each of them individually then parse them back into the object we expect
  const featureFlags = useAppSelector(state => {
    const flags = state.enabledFlags?.flags || [];

    const parsedFlags: Partial<Record<FeatureFlags["flags"][number], true>> = {};
    return flags.reduce((acc, flag) => {
      parsedFlags[flag] = true;
      return acc;
    }, parsedFlags);
  });
  const accessoryOnly = useAppSelector(state => state.accessoryOnly);
  const path = useAppSelector(state => state.path);

  //Once again, of course Swaps Grids do things completely differently
  const isSwapsGrid = useAppSelector(state => state.isSwapsGrid);
  const memoizedSwapsFilters = useAppSelector(swapsFiltersSelector);
  const swapsFilterOptions = useAppSelector(state => state.userProblems?.gridFilters);

  const filtersToUse = isSwapsGrid ? memoizedSwapsFilters : workingFilters;
  const filterOptionsToUse = isSwapsGrid ? swapsFilterOptions : filterOptions;

  const orderedFilters = useRef(
    new OrderedFilters(filtersToUse, filterOptionsToUse, shortlists, accessoryOnly, path, featureFlags, isSwapsGrid)
  );

  useEffect(() => {
    orderedFilters.current.onFilterChange(filtersToUse, filterOptionsToUse, shortlists);

    setDisplayedFilters(orderedFilters.current.getOrderedFilters());
    setDisplayedFilterCount(orderedFilters.current.getDisplayedFilterCount());
  }, [filtersToUse, filterOptionsToUse, shortlists]);

  return {
    displayedFilters,
    displayedFilterCount,
  };
};
